import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { calcIndexByOrder, removeFalsyKeys } from 'utils/common';
import { useCustomSearchParams } from 'hooks/useCustomSearchParams';
import { StyledActions, StyledButton } from 'styles/overrides';
import { Table } from 'components/table';
import { useQrBoxes } from 'api/qrBoxApi';
import { Notification } from 'components/Notification';
import { formatTime } from 'utils/timeUtils';
import { Modal, Avatar } from 'antd';
import { useDeleteQrBox } from 'api/qrBoxApi';
import { PATH_NAME_ACTION } from 'constants/routes';
import { Publish } from 'components/common';
import { Permission } from 'components/permission/Permission';
import { ROLE } from 'constants/roles';

export default function QRTable() {
  const intl = useIntl();
  const [search, setSearch] = useCustomSearchParams();
  const { data, isLoading, isFetching, refetch } = useQrBoxes({
    params: search,
  });
  const deleteMutation = useDeleteQrBox();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const columns = [
    {
      title: intl.formatMessage({ id: 'common.table.no' }),
      render: (text, record, index) => {
        return calcIndexByOrder({
          index,
          page: search.page,
          limit: search.page_size,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'views.qrBoxes.table.name' }),
      dataIndex: ['name'],
    },
    {
      title: intl.formatMessage({ id: 'views.qrBoxes.table.file' }),
      dataIndex: ['file', 'path'],
      render: (value) => <Avatar size={32} src={value} />,
    },
    {
      title: intl.formatMessage({ id: 'common.field.order' }),
      dataIndex: ['order'],
    },
    {
      title: intl.formatMessage({ id: 'common.table.published' }),
      dataIndex: 'published',
      render: (value) => <Publish published={value} />,
    },
    {
      title: intl.formatMessage({ id: 'common.table.createdAt' }),
      dataIndex: 'created_at',
      render: (value) => formatTime(value),
    },
    {
      render: ({ id }) => (
        <Link to={`${id}`}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.seeDetails' })}
          </StyledButton>
        </Link>
      ),
    },
  ];

  const handleDelete = () => {
    if (selectedRowKeys.length <= 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'common.modal.deleteTitle' }),
      okText: intl.formatMessage({ id: 'common.btn.confirm' }),
      onOk() {
        return deleteMutation.mutateAsync(selectedRowKeys, {
          onSuccess() {
            refetch();

            setSelectedRowKeys([]);
            Notification(
              'success',
              intl.formatMessage({ id: 'message.deleteSuccess' }),
            );
          },
          onError(error) {
            Notification(
              'error',
              error?.message ||
                intl.formatMessage({ id: 'message.commonError' }),
            );
          },
        });
      },
      onCancel() {},
    });
  };

  const handleChangeTable = ({ current: page, pageSize: limit }) => {
    setSearch(removeFalsyKeys({ ...search, page, page_size: limit }));
  };

  return (
    <>
      <StyledActions size={24}>
        <Link to={PATH_NAME_ACTION.CREATE}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.addNew' })}
          </StyledButton>
        </Link>

        <Permission roles={[ROLE.ADMIN]}>
          <StyledButton
            onClick={handleDelete}
            disabled={selectedRowKeys.length === 0}
          >
            {intl.formatMessage({ id: 'common.btn.delete' })}
          </StyledButton>
        </Permission>
      </StyledActions>

      <Table
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
          preserveSelectedRowKeys: true,
        }}
        columns={columns}
        dataSource={data || []}
        loading={isLoading || isFetching || deleteMutation.isLoading}
        pagination={false}
        onChange={handleChangeTable}
      />
    </>
  );
}
